/**
 * Built-in default poses shipped with the application.
 *
 * These are static JSON files under /poses/ that cannot be deleted or renamed.
 * They appear in the pose library alongside user-created cloud poses.
 */

import { type PoseFile, resolveBonePoses } from "./poseLibrary.ts";
import { useStore } from "./store.ts";

export interface DefaultPose {
  /** Unique identifier (matches filename without extension). */
  id: string;
  /** Display name shown in the library UI. */
  name: string;
  /** URL path to the pose JSON file. */
  url: string;
  /** Category for grouping in the UI. */
  category: "reference" | "locomotion" | "action";
}

/**
 * Registry of built-in poses. Order determines display order.
 */
export const DEFAULT_POSES: DefaultPose[] = [
  // Reference poses
  {
    id: "default:t-pose",
    name: "T-Pose",
    url: "/poses/t-pose.json",
    category: "reference",
  },
  {
    id: "default:a-pose",
    name: "A-Pose",
    url: "/poses/a-pose.json",
    category: "reference",
  },
  {
    id: "default:relaxed-standing",
    name: "Relaxed Standing",
    url: "/poses/relaxed-standing.json",
    category: "reference",
  },
  // Locomotion
  {
    id: "default:walk",
    name: "Walk",
    url: "/poses/walk.json",
    category: "locomotion",
  },
  {
    id: "default:run",
    name: "Run",
    url: "/poses/run.json",
    category: "locomotion",
  },
  {
    id: "default:jump",
    name: "Jump",
    url: "/poses/jump.json",
    category: "locomotion",
  },
  // Action
  {
    id: "default:sitting",
    name: "Sitting",
    url: "/poses/sitting.json",
    category: "action",
  },
  {
    id: "default:fighting-stance",
    name: "Fighting Stance",
    url: "/poses/fighting-stance.json",
    category: "action",
  },
  {
    id: "default:basketball",
    name: "Basketball",
    url: "/poses/basketball.json",
    category: "action",
  },
  {
    id: "default:dance",
    name: "Dance",
    url: "/poses/dance.json",
    category: "action",
  },
  {
    id: "default:crawling",
    name: "Crawling",
    url: "/poses/crawling.json",
    category: "action",
  },
  {
    id: "default:laying",
    name: "Laying",
    url: "/poses/laying.json",
    category: "action",
  },
  {
    id: "default:kneeling",
    name: "Kneeling",
    url: "/poses/kneeling.json",
    category: "action",
  },
];

/** Cache for fetched default pose files. */
const poseCache = new Map<string, PoseFile>();

/** Last successfully applied default pose name (for debug display). */
let _lastAppliedPresetName: string | null = null;

/** Returns the display name of the last applied default pose, or null. */
export function getLastAppliedPresetName(): string | null {
  return _lastAppliedPresetName;
}

/** Clear the last-applied preset (call when a non-preset pose is loaded). */
export function clearLastAppliedPresetName(): void {
  _lastAppliedPresetName = null;
}

/**
 * Load and apply a default pose to a mannequin.
 */
export async function applyDefaultPose(
  pose: DefaultPose,
  objectId: string,
): Promise<boolean> {
  try {
    let data = poseCache.get(pose.id);
    if (!data) {
      const res = await fetch(pose.url);
      if (!res.ok) return false;
      data = (await res.json()) as PoseFile;
      if (data.magic !== "POSEREF_POSE_V1") return false;
      poseCache.set(pose.id, data);
    }

    const poses = resolveBonePoses(data, objectId);

    // Reset first so bones not in this pose return to rest
    useStore.getState().resetPose(objectId);
    useStore.getState().setBonePoses(objectId, poses);
    _lastAppliedPresetName = pose.name;
    return true;
  } catch (e) {
    console.error("Failed to load default pose:", e);
    return false;
  }
}
